/* The income statement, as charts — §9.4's rule applies here too: hand-written
   SVG, no charting library.

   Nothing on this screen is a judgement. It reports what the company filed and
   what analysts had expected, and leaves the reading to you. */

import { useEffect, useState } from 'react';
import { daysUntil, moneyExact, pct, pctSigned, shares as sharesOf, shortDate } from '../lib/format';
import { ruleOf40 } from '../state/ruleOf40';
import { normaliseFinancials } from '../state/statementPeriods';
import { useStore } from '../state/store';
import type {
  DividendOutlook, EpsQuarter, Financials as FinancialsData, StatementPeriod,
} from '../types';

/** Money at statement scale. 1,305,000,000 reads as 1.31B, not as fifteen digits. */
function compact(v: number | null, currency = 'USD'): string {
  if (v === null) return '—';
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  const symbol = currency === 'USD' ? '$' : '';
  const suffix = currency === 'USD' ? '' : ` ${currency}`;
  if (abs >= 1e9) return `${sign}${symbol}${(abs / 1e9).toFixed(2)}B${suffix}`;
  if (abs >= 1e6) return `${sign}${symbol}${(abs / 1e6).toFixed(0)}M${suffix}`;
  if (abs >= 1e3) return `${sign}${symbol}${(abs / 1e3).toFixed(0)}K${suffix}`;
  return `${sign}${symbol}${abs.toFixed(0)}${suffix}`;
}

/** A period label: "2025" for a year end, "Q2 '26" for a quarter. */
function periodLabel(end: string, quarterly: boolean): string {
  const d = new Date(`${end}T00:00:00Z`);
  const year = d.getUTCFullYear();
  if (!quarterly) return String(year);
  return `Q${Math.floor(d.getUTCMonth() / 3) + 1} '${String(year).slice(2)}`;
}

/* ---------------- bars ---------------- */

interface Bar {
  /** The period's end date. Unique per period, so it is what the columns are
      keyed on — keyed on the label, a quarter that arrived twice gave two
      columns one key, and React kept old columns on screen after every switch. */
  key: string;
  label: string;
  value: number | null;
  note?: string;
}

/** Past this many columns a figure under each one no longer fits a phone's
    width and the figures ran into one another. Beyond it the chart reads out
    the column you tap, above the bars, and names every few periods below. */
const LABELLED_MAX = 6;

/** A column chart that copes with negative values by putting the baseline where
    zero actually falls, rather than at the bottom of the box. `tall` is the
    full-screen version: taller, and wide enough to name every column. */
function Bars({ bars, format, positiveColor, tall = false }: {
  bars: Bar[]; format: (v: number | null) => string; positiveColor: string; tall?: boolean;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const values = bars.map((b) => b.value).filter((v): v is number => v !== null);
  if (values.length === 0) return <div className="empty">Nothing reported.</div>;

  const hi = Math.max(...values, 0);
  const lo = Math.min(...values, 0);
  const span = hi - lo || 1;
  const H = tall ? 220 : 92;
  const zeroY = (hi / span) * H;
  const dense = !tall && bars.length > LABELLED_MAX;
  const every = dense ? Math.ceil(bars.length / LABELLED_MAX) : 1;
  // What the read-out shows: the tapped column, or the latest until one is.
  const shown = bars.find((b) => b.key === picked) ?? bars[bars.length - 1]!;

  return (
    <div className="fin-chart" data-dense={dense} data-tall={tall}>
      {dense ? (
        <div className="fin-readout">
          <span className="k">{shown.label}</span>
          <span className={`v num ${shown.value !== null && shown.value < 0 ? 'down' : ''}`}>
            {format(shown.value)}
          </span>
          {shown.note ? <span className="n num">{shown.note}</span> : null}
        </div>
      ) : null}
      <div className="fin-bars" style={{ height: H }}>
        {bars.map((b) => {
          const v = b.value;
          const height = v === null ? 0 : (Math.abs(v) / span) * H;
          const negative = v !== null && v < 0;
          return (
            <button type="button" key={b.key} className="fin-bar-slot"
                    aria-pressed={dense ? b.key === shown.key : undefined}
                    aria-label={`${b.label}: ${format(v)}`}
                    onClick={() => setPicked(b.key)}>
              <span
                className="fin-bar"
                style={{
                  height: Math.max(height, v === null ? 0 : 2),
                  top: negative ? zeroY : zeroY - height,
                  background: negative ? 'var(--down)' : positiveColor,
                }}
              />
            </button>
          );
        })}
        {lo < 0 ? <span className="fin-zero" style={{ top: zeroY }} /> : null}
      </div>
      <div className="fin-labels">
        {bars.map((b, i) => {
          // Every few periods, counted back from the latest so it is always named.
          const named = !dense || (bars.length - 1 - i) % every === 0;
          return (
            <div key={b.key} className="fin-label">
              {dense ? null : (
                <span className={`v num ${b.value !== null && b.value < 0 ? 'down' : ''}`}>
                  {format(b.value)}
                </span>
              )}
              <span className="k">{named ? b.label : '\u00a0'}</span>
              {!dense && b.note ? <span className="n num">{b.note}</span> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Revenue and net income side by side for each period, on one scale — how much
    of what came in was kept, which two separate charts with their own scales
    cannot show. */
function PairedBars({ periods, quarterly, currency }: {
  periods: StatementPeriod[]; quarterly: boolean; currency: string;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const values = periods.flatMap((p) => [p.revenue, p.netIncome]).filter((v): v is number => v !== null);
  if (values.length === 0) return <div className="empty">Nothing reported.</div>;

  const hi = Math.max(...values, 0);
  const lo = Math.min(...values, 0);
  const span = hi - lo || 1;
  const H = 220;
  const zeroY = (hi / span) * H;
  const column = (v: number | null, color: string) => {
    const height = v === null ? 0 : (Math.abs(v) / span) * H;
    const negative = v !== null && v < 0;
    return (
      <span className="fin-bar" style={{
        height: Math.max(height, v === null ? 0 : 2),
        top: negative ? zeroY : zeroY - height,
        background: negative ? 'var(--down)' : color,
      }} />
    );
  };
  const shown = periods.find((p) => p.end === picked) ?? periods[periods.length - 1]!;
  const margin = shown.revenue && shown.netIncome !== null && shown.revenue !== 0
    ? (shown.netIncome / shown.revenue) * 100
    : null;

  return (
    <div className="fin-chart" data-tall="true">
      <div className="fin-legend">
        <span><i style={{ background: 'var(--accent)' }} />Revenue</span>
        <span><i style={{ background: 'var(--up)' }} />Net income</span>
      </div>
      <div className="fin-readout">
        <span className="k">{periodLabel(shown.end, quarterly)}</span>
        <span className="v num">{compact(shown.revenue, currency)}</span>
        <span className={`v num ${shown.netIncome !== null && shown.netIncome < 0 ? 'down' : 'up'}`}>
          {compact(shown.netIncome, currency)}
        </span>
        {margin !== null ? <span className="n num">margin {pct(margin)}</span> : null}
      </div>
      <div className="fin-bars" style={{ height: H }}>
        {periods.map((p) => (
          <button type="button" key={p.end} className="fin-bar-slot fin-pair"
                  aria-pressed={p.end === shown.end}
                  aria-label={`${periodLabel(p.end, quarterly)}: revenue ${compact(p.revenue, currency)}, net income ${compact(p.netIncome, currency)}`}
                  onClick={() => setPicked(p.end)}>
            <span className="fin-pair-half">{column(p.revenue, 'var(--accent)')}</span>
            <span className="fin-pair-half">{column(p.netIncome, 'var(--up)')}</span>
          </button>
        ))}
        {lo < 0 ? <span className="fin-zero" style={{ top: zeroY }} /> : null}
      </div>
      <div className="fin-labels">
        {periods.map((p) => (
          <div key={p.end} className="fin-label">
            <span className="k">{periodLabel(p.end, quarterly)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- EPS against consensus ---------------- */

function EpsRecord({ eps }: { eps: EpsQuarter[] }) {
  if (eps.length === 0) return <div className="empty">No reported quarters on record.</div>;

  return (
    <div className="rows">
      {[...eps].reverse().map((q) => {
        const beat = q.estimate !== null && q.actual > q.estimate;
        const missed = q.estimate !== null && q.actual < q.estimate;
        // Yahoo gives the surprise as a fraction.
        const surprise = q.surprisePct === null ? null : q.surprisePct * 100;
        return (
          <div key={q.end} className="card card-pad eps-row">
            <div className="row-top">
              <span className="row-ticker" style={{ fontSize: 13, fontWeight: 500 }}>
                {periodLabel(q.end, true)}
              </span>
              {q.estimate !== null ? (
                <span className="tag" data-kind={beat ? 'ok' : missed ? 'over' : 'muted'}>
                  {beat ? 'BEAT' : missed ? 'MISSED' : 'IN LINE'}
                </span>
              ) : null}
              <span className="row-right">
                <span className={`num ${beat ? 'up' : missed ? 'down' : ''}`}
                      style={{ fontWeight: 600, fontSize: 14 }}>
                  {q.actual.toFixed(2)}
                </span>
                {surprise !== null ? (
                  <span className={`num ${surprise >= 0 ? 'up' : 'down'}`}
                        style={{ display: 'block', fontSize: 11.5, textAlign: 'right' }}>
                    {pctSigned(surprise)}
                  </span>
                ) : null}
              </span>
            </div>
            <div className="row-meta">
              <span className="num">reported {q.actual.toFixed(4)}</span>
              <span className="num">expected {q.estimate === null ? '—' : q.estimate.toFixed(4)}</span>
              <span>{shortDate(q.end)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- the section ---------------- */

/* How many periods to show. Eight is the useful default — two years of
   quarters, enough to see a trend without the bars turning into hairs — but
   four reads a turn faster and twelve shows a cycle. */
const DEPTHS = [4, 8, 12] as const;

/* What the company pays out, if it pays anything.
 *
 * Two things this is careful about. The dates Yahoo returns are perfectly happy
 * to be in the past — a company that has paid but not announced the next one
 * reports the last — so each date is called what it actually is rather than
 * always "next". And the figure on the summary is the *annual* rate per share,
 * not the size of the next payment: calling a quarterly payer's yearly rate the
 * upcoming dividend would overstate it fourfold. */
function DividendCard({ dividend, currency, shares }: {
  dividend: DividendOutlook | undefined;
  currency: string;
  shares?: number;
}) {
  // Zero is what a non-payer reports, and it is not a dividend.
  const rate = dividend?.perShare && dividend.perShare > 0 ? dividend.perShare : null;
  if (!dividend || (rate === null && dividend.payDate === null && dividend.exDate === null)) {
    return (
      <div className="card card-pad" style={{ marginBottom: 12 }}>
        <div className="kv">
          <span className="k">Dividend</span>
          <span className="v dim">none on record</span>
        </div>
      </div>
    );
  }

  const today = new Date().toISOString().slice(0, 10);
  const when = (date: string | null, noun: string) => {
    if (!date) return null;
    const ahead = date >= today;
    const away = daysUntil(date);
    return {
      label: `${ahead ? 'Next' : 'Last'} ${noun}`,
      value: shortDate(date),
      note: ahead && away >= 0 ? `in ${away} day${away === 1 ? '' : 's'}` : null,
    };
  };

  const ex = when(dividend.exDate, 'ex-date');
  const pay = when(dividend.payDate, 'payment');
  const yearly = rate;

  return (
    <div className="card card-pad" style={{ marginBottom: 12 }}>
      <div className="kv">
        <span className="k" style={{ fontWeight: 600, color: 'var(--text)' }}>Dividend</span>
        {dividend.yieldFraction !== null && dividend.yieldFraction > 0 ? (
          <span className="v num" style={{ marginLeft: 'auto' }}>
            {pct(dividend.yieldFraction * 100)} yield
          </span>
        ) : null}
      </div>

      {[ex, pay].map((row) => row ? (
        <div className="kv" key={row.label} style={{ marginTop: 6 }}>
          <span className="k">{row.label}</span>
          <span className="v num">{row.value}</span>
          {row.note ? <span className="dim" style={{ fontSize: 11 }}>{row.note}</span> : null}
        </div>
      ) : null)}

      {yearly !== null ? (
        <>
          <div className="kv" style={{ marginTop: 6 }}>
            <span className="k">Rate per share, a year</span>
            <span className="v num">{moneyExact(yearly, currency as never)}</span>
          </div>
          {shares && shares > 0 ? (
            <div className="kv">
              <span className="k">On your {sharesOf(shares)} shares, a year</span>
              <span className="v num">{moneyExact(yearly * shares, currency as never)}</span>
            </div>
          ) : null}
        </>
      ) : null}

      <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
        The rate is what the company pays across a year, not the size of one payment. Most split it
        into four. The ex-date is the one that decides entitlement: hold through it and the payment
        is yours.
      </div>
    </div>
  );
}

/* Growth plus margin against forty. Two numbers and their sum — the card never
   says whether the company is good, because that is not a thing arithmetic
   knows, and the rule was written for software companies in the first place. */
function RuleOf40Card({ quarterly }: { quarterly: StatementPeriod[] }) {
  const r = ruleOf40(quarterly);
  if (!r) {
    return (
      <div className="card card-pad" style={{ marginBottom: 12 }}>
        <div className="kv"><span className="k">Rule of 40</span>
          <span className="v dim">not enough filed</span></div>
        <div className="dim" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.45 }}>
          It needs two full trailing years of revenue to compare, and every quarter in them.
        </div>
      </div>
    );
  }

  return (
    <div className="card card-pad" style={{ marginBottom: 12 }}>
      <div className="kv">
        <span className="k">Rule of 40</span>
        <span className={`v num ${r.clears ? 'up' : 'down'}`}>
          {r.clears ? '▲' : '▼'} {r.total.toFixed(1)}
        </span>
      </div>
      <div className="kv">
        <span className="k">Revenue growth</span>
        <span className="v num">{pctSigned(r.growthPct)}</span>
      </div>
      <div className="kv">
        <span className="k">{r.marginKind === 'operating' ? 'Operating margin' : 'Net margin'}</span>
        <span className="v num">{pctSigned(r.marginPct)}</span>
      </div>
      <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
        Trailing twelve months to {shortDate(r.to)}, against the year before.
        {r.marginKind === 'net'
          ? ' Operating income was not filed, so this uses net margin, usually the lower of the two.'
          : ''}
        {' '}It is a software-company yardstick: a memory maker or a miner can miss it every year and
        still be a fine business.
      </div>
    </div>
  );
}

type Metric = 'revenue' | 'netIncome' | 'margin' | 'both';

const METRICS: Array<{ value: Metric; label: string }> = [
  { value: 'revenue', label: 'Revenue' },
  { value: 'netIncome', label: 'Net income' },
  { value: 'margin', label: 'Margin' },
  { value: 'both', label: 'Both' },
];

/** One reading of the periods as columns. */
function barsFor(metric: Exclude<Metric, 'both'>, periods: StatementPeriod[], quarterly: boolean): Bar[] {
  return periods.map((p, i) => {
    const bar: Bar = { key: p.end, label: periodLabel(p.end, quarterly), value: null };
    if (metric === 'revenue') {
      bar.value = p.revenue;
      const prior = periods[i - 1]?.revenue;
      if (prior && p.revenue !== null && prior !== 0) {
        bar.note = pctSigned(((p.revenue - prior) / Math.abs(prior)) * 100);
      }
    } else if (metric === 'netIncome') {
      bar.value = p.netIncome;
    } else {
      bar.value = p.revenue && p.netIncome !== null && p.revenue !== 0
        ? (p.netIncome / p.revenue) * 100
        : null;
    }
    return bar;
  });
}

export function FinancialsSection({ symbol, name, shares }: {
  symbol: string;
  name?: string;
  /** Known when this is shown from inside a position, so the payout can be put
      in terms of what is actually held. */
  shares?: number;
}) {
  const { loadFinancials } = useStore();
  const [data, setData] = useState<FinancialsData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'empty'>('loading');
  const [period, setPeriod] = useState<'annual' | 'quarterly'>('quarterly');
  const [depth, setDepth] = useState<number>(8);
  /** The chart opened across the whole screen, and which reading it shows. */
  const [full, setFull] = useState<Metric | null>(null);

  useEffect(() => {
    let live = true;
    setState('loading');
    void loadFinancials(symbol).then((result) => {
      if (!live) return;
      /* Cleaned on the way in, so every reader below — the charts and Rule of
         40's twelve-month sums alike — sees each period once. Copies saved on a
         device before the source was fixed are cleaned the same way. */
      const clean = result ? normaliseFinancials(result) : null;
      setData(clean);
      setState(clean && (clean.annual.length || clean.quarterly.length) ? 'ready' : 'empty');
    });
    return () => { live = false; };
  }, [symbol, loadFinancials]);

  /* While the chart fills the screen the page under it must not scroll, and
     Escape closes it on a keyboard. */
  useEffect(() => {
    if (full === null) return;
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFull(null); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = before;
      window.removeEventListener('keydown', onKey);
    };
  }, [full]);

  if (state === 'loading') {
    return (
      <section>
        <div className="subject-head">
          <h2>{name ?? symbol}</h2>
          <span className="meta">{symbol}</span>
        </div>
        <div className="empty">Reading the filings…</div>
      </section>
    );
  }

  if (state === 'empty' || !data) {
    return (
      <section>
        <div className="subject-head">
          <h2>{name ?? symbol}</h2>
          <span className="meta">{symbol}</span>
        </div>
        <div className="empty">
          No income statement came back for {symbol}. Foreign issuers and recent listings often
          have none on file.
        </div>
      </section>
    );
  }

  const all: StatementPeriod[] = period === 'annual' ? data.annual : data.quarterly;
  // The most recent `depth`, oldest first, so the bars read left to right.
  const periods = all.slice(-depth);
  const quarterly = period === 'quarterly';
  const money = (v: number | null) => compact(v, data.currency);
  const percent = (v: number | null) => (v === null ? '—' : pct(v));
  const expand = (metric: Metric, what: string) => (
    <button className="btn btn-ghost btn-sm" onClick={() => setFull(metric)}
            aria-label={`Expand ${what} to the full screen`}>
      Expand
    </button>
  );

  return (
    <section>
      {/* Whose numbers these are, before the numbers. A screen you reach from a
          picker, a search box and an earnings card alike cannot assume you
          remember which company you asked for. */}
      <div className="subject-head">
        <h2>{name ?? symbol}</h2>
        <span className="meta">{symbol} · {data.currency}</span>
      </div>
      <div className="dim" style={{ fontSize: 11.5, marginTop: -4, marginBottom: 12 }}>
        How the business is doing
      </div>

      <div className="seg" style={{ marginBottom: 10 }}>
        {(['quarterly', 'annual'] as const).map((p) => (
          <button key={p} aria-pressed={period === p} onClick={() => setPeriod(p)}>
            {p === 'quarterly' ? 'Quarterly' : 'Annual'}
          </button>
        ))}
      </div>

      <DividendCard dividend={data.dividend} currency={data.currency} shares={shares} />
      <RuleOf40Card quarterly={data.quarterly} />

      {/* A depth is offered only when it would show something a shorter one does
          not — a "12 quarters" button on a company that filed nine is a promise
          the data cannot keep. */}
      <div className="seg" style={{ marginBottom: 12 }}>
        {DEPTHS.map((d, i) => {
          const shorter = DEPTHS[i - 1] ?? 0;
          return (
            <button key={d} aria-pressed={depth === d} disabled={all.length <= shorter}
                    onClick={() => setDepth(d)}>
              {d} {quarterly ? 'quarters' : 'years'}
            </button>
          );
        })}
      </div>

      {periods.length === 0 ? (
        <div className="empty">Nothing filed for this period type.</div>
      ) : (
        <>
          <div className="card card-pad">
            <div className="fin-title-row">
              <div className="fin-title">Revenue</div>
              {expand('revenue', 'revenue')}
            </div>
            <Bars bars={barsFor('revenue', periods, quarterly)} format={money}
                  positiveColor="var(--accent)" />
          </div>

          <div className="card card-pad" style={{ marginTop: 8 }}>
            <div className="fin-title-row">
              <div className="fin-title">Net income</div>
              {expand('netIncome', 'net income')}
            </div>
            <Bars bars={barsFor('netIncome', periods, quarterly)} format={money}
                  positiveColor="var(--up)" />
          </div>

          <div className="card card-pad" style={{ marginTop: 8 }}>
            <div className="fin-title-row">
              <div className="fin-title">Profit margin</div>
              {expand('margin', 'profit margin')}
            </div>
            <Bars bars={barsFor('margin', periods, quarterly)} format={percent}
                  positiveColor="var(--up)" />
            {data.margins.gross !== null || data.margins.operating !== null ? (
              <div className="row-meta" style={{ marginTop: 12 }}>
                {data.margins.gross !== null ? (
                  <span className="num">gross {pct(data.margins.gross * 100)}</span>
                ) : null}
                {data.margins.operating !== null ? (
                  <span className="num">operating {pct(data.margins.operating * 100)}</span>
                ) : null}
                {data.margins.revenueGrowth !== null ? (
                  <span className="num">revenue growth {pctSigned(data.margins.revenueGrowth * 100)}</span>
                ) : null}
              </div>
            ) : null}
          </div>

          <button className="btn btn-ghost btn-sm btn-block" style={{ marginTop: 8 }}
                  onClick={() => setFull('both')}>
            Revenue and net income together ›
          </button>
        </>
      )}

      <div className="section-head" style={{ marginTop: 22 }}>
        <h2>Earnings per share</h2>
        <span className="meta">against what was expected</span>
      </div>
      <EpsRecord eps={data.eps} />

      {/* The chart across the whole screen: every column named and valued, room
          to scroll sideways through twelve of them, and revenue beside net
          income on one scale. */}
      {full !== null && periods.length > 0 ? (
        <div className="fin-full" role="dialog" aria-modal="true"
             aria-label={`${name ?? symbol}, ${quarterly ? 'quarterly' : 'annual'} figures`}>
          <div className="fin-full-head">
            <h2>{name ?? symbol} · {quarterly ? 'quarterly' : 'annual'}</h2>
            <button className="back" onClick={() => setFull(null)} aria-label="Close">✕</button>
          </div>
          <div className="seg">
            {METRICS.map((m) => (
              <button key={m.value} aria-pressed={full === m.value} onClick={() => setFull(m.value)}>
                {m.label}
              </button>
            ))}
          </div>
          <div className="fin-full-scroll" data-no-pull>
            <div className="fin-full-canvas"
                 style={{ minWidth: `max(100%, ${periods.length * (full === 'both' ? 64 : 56)}px)` }}>
              {full === 'both' ? (
                <PairedBars periods={periods} quarterly={quarterly} currency={data.currency} />
              ) : (
                <Bars tall bars={barsFor(full, periods, quarterly)}
                      format={full === 'margin' ? percent : money}
                      positiveColor={full === 'revenue' ? 'var(--accent)' : 'var(--up)'} />
              )}
            </div>
          </div>
          <div className="dim" style={{ fontSize: 11, textAlign: 'center' }}>
            Tap a column to read it. Turn the phone sideways for more room.
          </div>
        </div>
      ) : null}
    </section>
  );
}
